// ============================================================
// 6. StockMovementReportPage.tsx
// ============================================================
// src/features/reports/pages/StockMovementReportPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getBranches } from '@/features/settings/services/settingsApi';
import { getStockMovement, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { ReportFilters } from '../components/ReportFilters';
import { SummaryCard } from '../components/SummaryCard';
import {
    ArrowDownUp, ArrowUp, ArrowDown, Package,
    User, Calendar, GitBranch, Activity
} from 'lucide-react';

const transactionColors: Record<string, string> = {
    sale: 'from-blue-500 to-indigo-600',
    purchase: 'from-emerald-500 to-teal-600',
    transfer_in: 'from-purple-500 to-violet-600',
    transfer_out: 'from-amber-500 to-orange-600',
    adjustment: 'from-slate-500 to-slate-600',
    return: 'from-rose-500 to-pink-600',
    damage: 'from-red-500 to-rose-600',
};

export function StockMovementReportPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
    const [branchId, setBranchId] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [productId, setProductId] = useState('');

    const { data: branches } = useQuery({
        queryKey: ['branches-report', tenantId],
        queryFn: () => getBranches(tenantId),
        enabled: !!tenantId,
    });

    const params = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        branchId: branchId || undefined,
        productId: productId || undefined,
    };

    const { data, isLoading } = useQuery({
        queryKey: ['report-stock-movement', tenantId, params],
        queryFn: () => getStockMovement(tenantId, params),
        enabled: !!tenantId,
    });

    const formatNumber = (val: string | number) => parseFloat(String(val || '0')).toLocaleString();
    const branchesList = (branches || []) as any[];

    const setQuickDate = (days: number) => {
        const end = new Date();
        const start = new Date();
        if (days === 0) start.setHours(0, 0, 0, 0);
        else start.setDate(start.getDate() - days);
        setDateRange({ startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] });
    };

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'stock_movement', format, params as any);
        window.open(url, '_blank');
    };

    const hasFilters = !!(dateRange.startDate || dateRange.endDate || branchId || productId);
    const clearFilters = () => { setDateRange({ startDate: '', endDate: '' }); setBranchId(''); setProductId(''); };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50/30">
            <ReportHeader
                title="Stock Movement"
                description="Inventory transaction history"
                icon={ArrowDownUp}
                iconGradient="from-cyan-500 to-blue-600"
                hasFilters={hasFilters}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
                branchName={branchesList.find(b => b.id === branchId)?.name}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                <ReportFilters
                    visible={showFilters}
                    dateRange={dateRange}
                    branchId={branchId}
                    branches={branchesList}
                    onDateRangeChange={setDateRange}
                    onBranchChange={setBranchId}
                    onClear={clearFilters}
                    onQuickDate={setQuickDate}
                    additionalFilters={
                        <input
                            type="text"
                            placeholder="Product ID (optional)"
                            value={productId}
                            onChange={e => setProductId(e.target.value)}
                            className="px-3 py-1.5 border rounded-lg text-xs w-48"
                        />
                    }
                />

                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data ? (
                    <EmptyState
                        icon={<ArrowDownUp className="w-10 h-10" />}
                        title="No Stock Movements"
                        description="No inventory transactions found for the selected period."
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <SummaryCard
                                label="Total Transactions"
                                value={String(data.summary?.totalTransactions || 0)}
                                icon={Activity}
                                gradient="from-cyan-500 to-blue-600"
                            />
                            <SummaryCard
                                label="Stock In"
                                value={formatNumber(data.summary?.totalIn || 0)}
                                icon={ArrowUp}
                                gradient="from-emerald-500 to-teal-600"
                            />
                            <SummaryCard
                                label="Stock Out"
                                value={formatNumber(data.summary?.totalOut || 0)}
                                icon={ArrowDown}
                                gradient="from-rose-500 to-pink-600"
                            />
                            <SummaryCard
                                label="Net Movement"
                                value={formatNumber((data.summary?.totalIn || 0) - (data.summary?.totalOut || 0))}
                                icon={GitBranch}
                                gradient="from-purple-500 to-violet-600"
                            />
                        </div>

                        {/* Transaction Type Breakdown */}
                        {data.typeBreakdown && data.typeBreakdown.length > 0 && (
                            <Card>
                                <CardContent className="p-5">
                                    <h3 className="text-sm font-semibold text-slate-800 mb-4">Transaction Type Breakdown</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                                        {data.typeBreakdown.map((type: any, i: number) => {
                                            const gradient = transactionColors[type.type] || 'from-slate-500 to-slate-600';
                                            return (
                                                <div key={i} className={`p-3 rounded-xl bg-gradient-to-br ${gradient} bg-opacity-10`}>
                                                    <p className="text-xs font-medium text-white/80 capitalize">{type.type.replace(/_/g, ' ')}</p>
                                                    <p className="text-lg font-bold text-white mt-1">{type.count}</p>
                                                    <p className="text-xs text-white/70">{formatNumber(type.totalQty)} units</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        <Card className="overflow-hidden">
                            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                                <h3 className="text-sm font-semibold text-slate-800">Transaction History</h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Product</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Type</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Quantity</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Branch</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Performed By</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Date</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {data.transactions?.map((tx: any, i: number) => (
                                            <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                <td className="px-6 py-4 text-sm font-medium text-slate-900">
                                                    {tx.productName}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <Badge
                                                        variant={
                                                            tx.transactionType === 'sale' ? 'info' :
                                                                tx.transactionType === 'purchase' ? 'success' :
                                                                    tx.transactionType?.includes('transfer') ? 'warning' :
                                                                        tx.transactionType === 'return' ? 'danger' : 'neutral'
                                                        }
                                                        size="sm"
                                                    >
                                                        {tx.transactionType?.replace(/_/g, ' ') || 'Unknown'}
                                                    </Badge>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <span className={`text-sm font-semibold ${parseFloat(tx.quantity) > 0 ? 'text-emerald-600' : 'text-rose-600'
                                                        }`}>
                                                        {parseFloat(tx.quantity) > 0 ? '+' : ''}{formatNumber(tx.quantity)}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-slate-500">{tx.branchName}</td>
                                                <td className="px-6 py-4 text-sm text-slate-500">{tx.performerName}</td>
                                                <td className="px-6 py-4 text-sm text-slate-500">
                                                    {new Date(tx.createdAt).toLocaleString()}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-slate-400 max-w-[200px] truncate">
                                                    {tx.notes || '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}